/**
 * CONTROLE DE TRAFO EM REFORMA — as regras do fluxo, num lugar só.
 *
 * O caminho do transformador na oficina (fluxograma "Módulo Controle Trafo em
 * Reforma"):
 *
 *   1. Entrada do trafo
 *   2. Coleta das informações da placa + definição dos serviços
 *   3. Ensaios elétricos + coleta de óleo
 *   4. Registro e andamento dos serviços (status)
 *   5. Ensaios + inspeção final (Qualidade)  →  concluído
 *
 * Este arquivo é PURO (sem tela, sem banco): o servidor (server/trafos.ts) e a
 * tela (src/trafos.ts) importam daqui as mesmas listas e a mesma conferência do
 * que falta para concluir cada etapa — assim o botão "Concluir etapa" e a
 * recusa do servidor nunca discordam.
 *
 * ARQUIVO IGUAL no sistemaColaborador (app "Operacional") e no
 * sistemaOperacional (Gestão da Operação). Mudou aqui, copie para o outro.
 * Os vocabulários (dados de placa, serviços, ensaios, itens de inspeção e
 * análises de óleo) são os mesmos do relatório técnico de transformador do
 * sistema de Relatórios.
 */

/* ------------------------------------------------------------- etapas --- */

export const ETAPAS = ["entrada", "placa", "ensaios", "servicos", "qualidade"] as const;
export type Etapa = (typeof ETAPAS)[number];
/** Etapa "depois da última": aprovado pela Qualidade e liberado. */
export const CONCLUIDO = "concluido";
export type EtapaOuConcluido = Etapa | typeof CONCLUIDO;

export interface EtapaInfo {
  key: Etapa;
  numero: number;
  /** Nome completo (cabeçalho da etapa). */
  titulo: string;
  /** Nome curto (passos, painel). */
  curto: string;
  /** O que se faz nesta etapa — a linha de ajuda. */
  ajuda: string;
}

export const ETAPAS_INFO: EtapaInfo[] = [
  {
    key: "entrada",
    numero: 1,
    titulo: "Entrada do trafo",
    curto: "Entrada",
    ajuda: "Registre a chegada: cliente, OS, nota fiscal e como o trafo chegou. Fotografe avarias e vazamentos antes de mexer.",
  },
  {
    key: "placa",
    numero: 2,
    titulo: "Coleta das informações da placa + definição dos serviços",
    curto: "Placa e serviços",
    ajuda: "Copie os dados da placa de identificação (fotografe a placa) e marque os serviços que serão executados.",
  },
  {
    key: "ensaios",
    numero: 3,
    titulo: "Ensaios elétricos + coleta de óleo",
    curto: "Ensaios e óleo",
    ajuda: "Resultado de cada ensaio elétrico de entrada e a coleta da amostra de óleo para o Laboratório.",
  },
  {
    key: "servicos",
    numero: 4,
    titulo: "Registro e andamento dos serviços",
    curto: "Serviços",
    ajuda: "Atualize o status de cada serviço conforme avança e registre o andamento com fotos.",
  },
  {
    key: "qualidade",
    numero: 5,
    titulo: "Ensaios + inspeção final (Qualidade)",
    curto: "Qualidade",
    ajuda: "Ensaios finais e inspeção de qualidade. Aprovado, o trafo é concluído; com problema, volta para os serviços.",
  },
];

/** Posição da etapa no fluxo (0–4; concluído = 5; desconhecida = -1). */
export function indiceEtapa(e: string | null | undefined): number {
  if (e === CONCLUIDO) return ETAPAS.length;
  return ETAPAS.indexOf(e as Etapa);
}

export function ehEtapa(e: unknown): e is Etapa {
  return typeof e === "string" && (ETAPAS as readonly string[]).includes(e);
}

export function infoEtapa(e: string | null | undefined): EtapaInfo | null {
  return ETAPAS_INFO.find((x) => x.key === e) ?? null;
}

/** Nome da etapa para texto corrido ("Placa e serviços", "Concluído"). */
export function nomeEtapa(e: string | null | undefined): string {
  if (e === CONCLUIDO) return "Concluído";
  return infoEtapa(e)?.curto ?? "—";
}

/* ---------------------------------------------------------- vocabulário --- */

/** Dados da placa de identificação (mesmos do relatório técnico). */
export const CAMPOS_PLACA: { key: string; label: string; placeholder?: string; opcoes?: string[] }[] = [
  { key: "fabricante", label: "Fabricante", placeholder: "ex.: WEG" },
  { key: "numero_serie", label: "Número de série" },
  { key: "ano_fabricacao", label: "Ano / data de fabricação", placeholder: "ex.: 2015" },
  { key: "potencia_kva", label: "Potência (kVA)", placeholder: "ex.: 500" },
  { key: "tensao_superior", label: "Tensão superior (kV)", placeholder: "ex.: 13,8" },
  { key: "tensao_inferior", label: "Tensão inferior (V)", placeholder: "ex.: 380/220" },
  { key: "impedancia", label: "Impedância (%)", placeholder: "ex.: 4,5" },
  { key: "ligacao", label: "Ligação / grupo", placeholder: "ex.: Dyn1" },
  { key: "frequencia", label: "Frequência (Hz)", placeholder: "ex.: 60" },
  { key: "fases", label: "Fases", opcoes: ["Trifásico", "Monofásico"] },
  { key: "isolamento", label: "Tipo", opcoes: ["A óleo", "A seco"] },
  {
    key: "tipo_oleo",
    label: "Fluido isolante",
    opcoes: [
      "Mineral tipo A (naftênico)",
      "Mineral tipo B (parafínico)",
      "Mineral tipo C (misto)",
      "Vegetal",
      "Silicone",
      "Outro",
    ],
  },
  { key: "volume_oleo", label: "Volume de óleo (L)" },
  { key: "massa_total", label: "Massa total (kg)" },
];
export const CHAVES_PLACA: string[] = CAMPOS_PLACA.map((c) => c.key);

/** Serviços mais comuns da reforma (o resto entra como "outro serviço"). */
export const SERVICOS_PADRAO: string[] = [
  "Desmontagem e abertura do tanque",
  "Limpeza geral",
  "Secagem da parte ativa (estufa)",
  "Rebobinamento dos enrolamentos",
  "Substituição de buchas",
  "Substituição de juntas e vedações",
  "Troca ou reparo do comutador",
  "Troca de componentes / acessórios",
  "Retirada de vazamento",
  "Tratamento termo vácuo do óleo",
  "Regeneração do óleo",
  "Substituição do óleo",
  "Complemento de nível de óleo",
  "Reaperto geral",
  "Jateamento e pintura",
  "Montagem final",
];

export const STATUS_SERVICO = ["Pendente", "Em andamento", "Concluído", "Cancelado"] as const;
export type StatusServico = (typeof STATUS_SERVICO)[number];
/** Status que encerram o serviço (contam como "feito" no andamento). */
export const SERVICO_ENCERRADO: readonly string[] = ["Concluído", "Cancelado"];

/** Ensaios elétricos de ENTRADA (etapa 3). */
export const ENSAIOS_ENTRADA: string[] = [
  "Resistência de isolamento (megôhmetro)",
  "Resistência ôhmica dos enrolamentos",
  "Relação de transformação (TTR)",
  "Fator de potência do isolamento",
  "Corrente de excitação",
];

/** Ensaios FINAIS (etapa 5) — os de entrada mais os de aceitação. */
export const ENSAIOS_FINAIS: string[] = [
  ...ENSAIOS_ENTRADA,
  "Tensão aplicada",
  "Tensão induzida",
  "Estanqueidade (vazamento)",
];

export const RESULTADOS_ENSAIO = ["Aprovado", "Reprovado", "Não realizado"] as const;

/** Análises da amostra de óleo (mesmas siglas do relatório técnico). */
export const ANALISES_OLEO: { key: string; label: string }[] = [
  { key: "FQ", label: "FQ · Físico-química" },
  { key: "CR", label: "CR · Cromatografia" },
  { key: "PCB", label: "PCB" },
  { key: "2-FAL", label: "2-FAL · Furfuraldeído" },
];
export const CHAVES_ANALISE: string[] = ANALISES_OLEO.map((a) => a.key);

export const COLETA_APLICA = ["Sim", "Não se aplica"] as const;

/** Itens da inspeção final (as "Inspeções Gerais" do relatório técnico + 2). */
export const INSPECAO_FINAL: string[] = [
  "Indicador de nível de óleo",
  "Tampa principal",
  "Relé de gás (Buchholz)",
  "Tampa de inspeção",
  "Termômetro de óleo",
  "Buchas AT e BT",
  "Válvula de alívio de pressão",
  "Pintura",
  "Relé de pressão súbita",
  "Comutador",
  "Tubo de explosão",
  "Vedações",
  "Tanque de expansão",
  "Registros",
  "Sílica gel",
  "Nível de óleo",
  "Radiadores",
  "Aterramento",
  "Placa de identificação fixada",
  "Sem vazamentos",
];

export const RESPOSTAS_INSPECAO = ["Conforme", "Não conforme", "Não se aplica"] as const;

/* ---------------------------------------------------------------- tipos --- */

export interface Servico {
  id: string;
  nome: string;
  status: StatusServico;
  obs: string;
  atualizado_por: string | null;
  atualizado_em: string | null;
}

export interface Ensaio {
  ensaio: string;
  /** "" = ainda não preenchido. */
  resultado: string;
  valores: string;
  obs: string;
}

export interface ColetaOleo {
  aplica?: string;
  data?: string | null;
  analises?: string[];
  amostra?: string;
  temperatura?: string;
  obs?: string;
}

export interface ItemInspecao {
  item: string;
  resposta: string;
  obs: string;
}

/** O que a conferência de etapa precisa saber do trafo. */
export interface TrafoParaConferir {
  cliente?: string | null;
  data_entrada?: string | null;
  placa?: Record<string, unknown> | null;
  servicos?: Servico[] | null;
  ensaios_entrada?: Ensaio[] | null;
  coleta_oleo?: ColetaOleo | null;
  ensaios_finais?: Ensaio[] | null;
  inspecao_final?: ItemInspecao[] | null;
}

/* ------------------------------------------------- conferência da etapa --- */

const vazio = (v: unknown) => String(v ?? "").trim() === "";

function lista<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : [];
}

/** Ensaios sem resultado escolhido (inclui os da lista padrão que nem existem ainda). */
function ensaiosSemResultado(ensaios: Ensaio[], padrao: string[]): string[] {
  const porNome = new Map(ensaios.map((e) => [e.ensaio, e]));
  const faltam = padrao.filter((nome) => vazio(porNome.get(nome)?.resultado));
  // Ensaio a mais (fora da lista padrão) também precisa de resultado.
  for (const e of ensaios) if (!padrao.includes(e.ensaio) && vazio(e.resultado)) faltam.push(e.ensaio);
  return faltam;
}

/**
 * O que falta para CONCLUIR a etapa. Lista vazia = pode concluir.
 * As frases aparecem para a pessoa, por isso são curtas e dizem o que fazer.
 */
export function pendenciasDaEtapa(t: TrafoParaConferir, etapa: Etapa): string[] {
  const p: string[] = [];
  switch (etapa) {
    case "entrada": {
      if (vazio(t.cliente)) p.push("Informe o cliente.");
      if (vazio(t.data_entrada)) p.push("Informe a data de entrada.");
      break;
    }
    case "placa": {
      const placa = t.placa ?? {};
      const temDado = CHAVES_PLACA.some((k) => !vazio(placa[k]));
      if (!temDado && placa.sem_placa !== true) {
        p.push('Preencha os dados da placa (ou marque "Placa ausente ou ilegível").');
      }
      if (lista(t.servicos).length === 0) p.push("Defina ao menos um serviço a executar.");
      break;
    }
    case "ensaios": {
      const faltam = ensaiosSemResultado(lista(t.ensaios_entrada), ENSAIOS_ENTRADA);
      if (faltam.length > 0) p.push(`Escolha o resultado de: ${faltam.join(", ")}.`);
      const c = t.coleta_oleo ?? {};
      if (vazio(c.aplica)) p.push('Diga se houve coleta de óleo ("Sim" ou "Não se aplica").');
      else if (c.aplica === "Sim") {
        if (vazio(c.data)) p.push("Informe a data da coleta de óleo.");
        if (lista(c.analises).length === 0) p.push("Marque as análises pedidas para a amostra de óleo.");
      }
      break;
    }
    case "servicos": {
      const servicos = lista(t.servicos);
      if (servicos.length === 0) p.push("Nenhum serviço definido para este trafo.");
      const abertos = servicos.filter((s) => !SERVICO_ENCERRADO.includes(s.status));
      if (abertos.length > 0) {
        p.push(
          `Ainda ${abertos.length === 1 ? "falta concluir 1 serviço" : `faltam concluir ${abertos.length} serviços`}: ` +
            abertos.map((s) => s.nome).join(", ") + ".",
        );
      }
      break;
    }
    case "qualidade": {
      // Serviço reaberto ou acrescentado depois da etapa 4 também segura a
      // aprovação: não se libera trafo com serviço por fazer.
      const abertos = lista(t.servicos).filter((s) => !SERVICO_ENCERRADO.includes(s.status));
      if (abertos.length > 0) {
        p.push(`Serviço ainda aberto (${abertos.map((s) => s.nome).join(", ")}) — devolva o trafo para os serviços.`);
      }
      const ensaios = lista(t.ensaios_finais);
      const faltam = ensaiosSemResultado(ensaios, ENSAIOS_FINAIS);
      if (faltam.length > 0) p.push(`Escolha o resultado de: ${faltam.join(", ")}.`);
      const reprovados = ensaios.filter((e) => e.resultado === "Reprovado").map((e) => e.ensaio);
      if (reprovados.length > 0) {
        p.push(`Ensaio reprovado (${reprovados.join(", ")}) — devolva o trafo para os serviços.`);
      }
      const itens = lista(t.inspecao_final);
      const porItem = new Map(itens.map((i) => [i.item, i]));
      const semResposta = INSPECAO_FINAL.filter((nome) => vazio(porItem.get(nome)?.resposta));
      if (semResposta.length > 0) {
        p.push(`Responda a inspeção final: ${semResposta.length} item(ns) sem resposta.`);
      }
      const nc = itens.filter((i) => i.resposta === "Não conforme");
      if (nc.length > 0) {
        p.push(`Inspeção com não conformidade (${nc.map((i) => i.item).join(", ")}) — devolva o trafo para os serviços.`);
      }
      break;
    }
  }
  return p;
}
